import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAlert } from 'contexts/AlertContext';
import { MapContainer } from 'components/map';
import { SafeReturnRepository, SafeReturnSessionResponse } from 'repository/safeReturnRepository';
import { AppScreen, Bleed, Button, ButtonFooter, Section, SectionHeader } from 'components/ds';
import styles from './SafeReturnWatchRequest.module.css';

export const SafeReturnWatchRequest: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId: string }>();
    const { showAlert } = useAlert();

    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState<SafeReturnSessionResponse | null>(null);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const currentUserNo = Number(sessionStorage.getItem('userNo')) || 0;

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        SafeReturnRepository.onGetSession(sessionId)
            .then((res: any) => {
                if (cancelled) return;
                const data: SafeReturnSessionResponse | undefined = res?.data?.data || res?.data;
                if (!data || !data.sessionId) {
                    setError(t('safeReturn.sessionNotFound'));
                    return;
                }
                setSession(data);
            })
            .catch(() => setError(t('safeReturn.sessionLoadFail')))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [sessionId]);

    // 이미 응답했거나 종료된 세션 여부
    const myWatch = session?.watchers.find(w => w.watcherUserNo === currentUserNo);
    const isTerminal = session && session.status !== 'ACTIVE';
    const alreadyResponded = myWatch && myWatch.status !== 'INVITED';

    const handleAccept = async () => {
        if (!sessionId) return;
        setProcessing(true);
        try {
            await SafeReturnRepository.onAcceptWatch(sessionId);
            navigate(`/safe-return/session/${sessionId}`, { replace: true });
        } catch (e) {
            console.error('[SafeReturnWatchRequest] 수락 실패', e);
            showAlert({ message: t('safeReturn.acceptFail') });
        } finally {
            setProcessing(false);
        }
    };

    const handleDecline = async () => {
        if (!sessionId) return;
        setProcessing(true);
        try {
            await SafeReturnRepository.onDeclineWatch(sessionId);
            navigate(-1);
        } catch (e) {
            console.error('[SafeReturnWatchRequest] 거절 실패', e);
            showAlert({ message: t('safeReturn.declineFail') });
        } finally {
            setProcessing(false);
        }
    };

    const canAnswer = !!session && !isTerminal && !alreadyResponded;

    return (
        <AppScreen title={t('safeReturn.title')} bar="sub" hasFooter={canAnswer}>
            {loading && <p className={styles.state}>{t('common.loading')}</p>}
            {!loading && error && <p className={styles.state}>{error}</p>}

            {!loading && session && (
                <>
                    {/* 누가 부탁했는지 */}
                    <div className={styles.asker}>
                        <span className={styles.avatar}>
                            {session.travelerProfileUrl
                                ? <img src={session.travelerProfileUrl} alt="" />
                                : (session.travelerNickname[0] || '?')}
                        </span>
                        <span className={styles.askerBody}>
                            <span className={styles.askerName}>{session.travelerNickname}</span>
                            <span className={styles.askerMsg}>
                                {t('safeReturn.travelerMsg1')} {t('safeReturn.travelerMsg2')}
                            </span>
                        </span>
                    </div>

                    {/* 어디로 가는지 */}
                    <Section spacing="tight">
                        <SectionHeader title={t('safeReturn.dest')} />
                        <p className={styles.dest}>{session.destAddress || t('safeReturn.noAddress')}</p>
                        {session.destLat && session.destLng && (
                            <Bleed>
                                <MapContainer
                                    center={{ lat: session.destLat, lng: session.destLng }}
                                    markers={[{
                                        id: 'dest',
                                        lat: session.destLat,
                                        lng: session.destLng,
                                        label: '',
                                        tooltip: session.destAddress,
                                        isDestination: true,
                                    }]}
                                    height={200}
                                    zoomLevel={3}
                                    placeName={session.destAddress}
                                />
                            </Bleed>
                        )}
                    </Section>

                    {/* 이미 끝났거나 답했으면 */}
                    {(isTerminal || alreadyResponded) && (
                        <Section divided last>
                            <p className={styles.notice}>
                                {isTerminal
                                    ? t('safeReturn.sessionEnded')
                                    : myWatch?.status === 'ACCEPTED'
                                        ? t('safeReturn.alreadyAccepted')
                                        : t('safeReturn.alreadyResponded')}
                            </p>
                            {myWatch?.status === 'ACCEPTED' && !isTerminal && (
                                <div className={styles.goAction}>
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        block
                                        onClick={() => navigate(`/safe-return/session/${sessionId}`, { replace: true })}
                                    >
                                        {t('safeReturn.goSeeLocation')}
                                    </Button>
                                </div>
                            )}
                        </Section>
                    )}

                    {canAnswer && (
                        <ButtonFooter>
                            <Button variant="ghost" size="lg" onClick={handleDecline} disabled={processing}>
                                {t('safeReturn.later')}
                            </Button>
                            <Button variant="primary" size="lg" block onClick={handleAccept} disabled={processing}>
                                {t('safeReturn.willWatch')}
                            </Button>
                        </ButtonFooter>
                    )}
                </>
            )}
        </AppScreen>
    );
};

export default SafeReturnWatchRequest;
